import { useEffect, useSyncExternalStore, type CSSProperties } from "react";
import type { Icon as PhosphorIcon, IconWeight } from "@phosphor-icons/react";
import {
  ArrowLeft,
  CaretDown,
  Check,
  CheckCircle,
  ClockCounterClockwise,
  Compass,
  Copy,
  EnvelopeOpen,
  EnvelopeSimple,
  Eye,
  EyeSlash,
  Info,
  LockKey,
  MagnifyingGlass,
  MapPinArea,
  MapTrifold,
  Moon,
  PaperPlaneTilt,
  Question,
  ShieldStar,
  SignIn,
  SignOut,
  SquaresFour,
  Sun,
  User,
  UserCircle,
  Warning,
  WarningCircle,
  Wrench,
  X,
} from "@phosphor-icons/react";

// ---------------------------------------------------------------------------
// Die Registry ist zweigeteilt.
//
// Jedes Phosphor-Modul enthaelt alle sechs Strichstaerken als eingebettete
// React-Elemente in einem einzigen new Map([...])-Literal. Weil das ein
// atomarer Ausdruck ist, kann der Buendler die ungenutzten Staerken nicht
// wegschuetteln — die Anwendung zeichnet aber nur "regular" (Ausnahme:
// "star-fill"). Bei 175 Symbolen waren das rund 576 KB roh im modulepreload
// des Eingangs, noch VOR dem ersten Bild der Anmeldeseite.
//
// Deshalb: nur die Symbole, die auf den ersten Bildschirmen wirklich
// vorkommen, bleiben fest verdrahtet (per grep aus src/app/**,
// screens/LoginScreen.tsx, components/ui/** und components/SeitenSkelett.tsx
// ermittelt, nicht geraten). Der Rest liegt in IconNachzuegler.tsx und kommt
// per import() nach. Bis dahin haelt <Icon> einen unsichtbaren Platzhalter
// GLEICHER Kantenlaenge, damit beim Eintreffen nichts springt.
// ---------------------------------------------------------------------------
const SOFORT: Record<string, PhosphorIcon> = {
  "arrow-left": ArrowLeft,
  "caret-down": CaretDown,
  compass: Compass,
  check: Check,
  "check-circle": CheckCircle,
  "clock-counter-clockwise": ClockCounterClockwise,
  copy: Copy,
  "envelope-open": EnvelopeOpen,
  "envelope-simple": EnvelopeSimple,
  eye: Eye,
  "eye-slash": EyeSlash,
  info: Info,
  "lock-key": LockKey,
  "magnifying-glass": MagnifyingGlass,
  "map-pin-area": MapPinArea,
  "map-trifold": MapTrifold,
  moon: Moon,
  "paper-plane-tilt": PaperPlaneTilt,
  question: Question,
  "shield-star": ShieldStar,
  "sign-in": SignIn,
  "sign-out": SignOut,
  "squares-four": SquaresFour,
  sun: Sun,
  user: User,
  "user-circle": UserCircle,
  warning: Warning,
  "warning-circle": WarningCircle,
  wrench: Wrench,
  x: X,
};

/** Zweite Haelfte; null, solange das Modul noch unterwegs ist. */
let nachzuegler: Record<string, PhosphorIcon> | null = null;
let holt: Promise<void> | null = null;
let fassung = 0;
const wecker = new Set<() => void>();

// ---------------------------------------------------------------------------
// Rueckweg, wenn der Nachzuegler nicht ankommt.
//
// Sein catch loeste frueher nur die Sperre und protokollierte. Wer danach kein
// weiteres Symbol aufschlug, sah die zweite Haelfte die GANZE Sitzung nicht
// mehr — und das trifft ausgerechnet Namen, die auf mitgebuendelten
// Bildschirmen sitzen, aber nicht in SOFORT stehen: arrow-right, chart-bar,
// sliders-horizontal und sparkle in app/CommandBar.tsx, dazu layout, plus,
// rows, seal-check und vector-two in screens/dashboard/ProjectOverview.tsx und
// arrows-clockwise in App.tsx. Die Befehlszeile waere dann eine Liste ohne
// Sinnbilder, die Projektuebersicht eine Kachelreihe mit Luecken.
//
// Warum nicht stattdessen die vier in SOFORT ziehen: ein Phosphor-Modul kostet
// alle sechs Strichstaerken auf einmal, nach der Rechnung oben rund 3,3 KB roh
// je Symbol. Vier waeren ~13 KB, die zehn oben ~33 KB — im modulepreload des
// Eingangs, also genau in dem Pfad, den die Teilung schuetzen soll. Und es
// heilte nur die aufgenommenen Namen; jeder spaetere Bildschirm haette
// dieselbe Luecke wieder. Der Wiederholungsweg kostet nichts und deckt alle.
//
// Bewusst NICHT ueber retryOnStaleShell aus lib/chunk: der laedt bei einem
// 404 die ganze Seite neu. Fuer einen Bildschirm ist das richtig, fuer ein
// Sinnbild nicht — es wuerfe nebenbei jede offene Eingabe im Mangel-Formular
// weg. Ein Symbol darf niemals ein Neuladen ausloesen.
// ---------------------------------------------------------------------------
/** Wachsende Abstaende nach einem Fehlschlag; danach ruht der Zeitgeber. */
const WIEDERHOLUNGEN = [1000, 2000, 4000, 8000];
/** Ist der Vorrat aufgebraucht, darf erst nach dieser Ruhe neu begonnen werden. */
const RUHE_MS = 30_000;
let fehlschlaege = 0;
/** Bis dahin laeuft ein Zeitgeber bzw. die Ruhe — kein zweiter Anlauf. */
let sperreBis = 0;

function nachladen(): Promise<void> {
  if (nachzuegler) return Promise.resolve();
  if (holt) return holt;
  // Nach einem Fehlschlag wartet der Zeitgeber unten schon. Bis er drankommt,
  // darf ein frisch aufgeschlagenes Symbol keinen eigenen Anlauf starten —
  // sonst wuerde aus dem Rueckweg bei jedem Mounten eine Anfrageschleife.
  if (Date.now() < sperreBis) return Promise.resolve();
  holt = import("./IconNachzuegler")
    .then((m) => {
      nachzuegler = m.NACHZUEGLER;
      fehlschlaege = 0;
      sperreBis = 0;
      fassung += 1;
      // Alle stehenden Platzhalter wecken — ohne das blieben sie leer, bis
      // irgendetwas anderes ein Neuzeichnen ausloest.
      wecker.forEach((w) => w());
      if (import.meta.env.DEV) registryPruefen();
    })
    .catch((e: unknown) => {
      // Netz weg oder altes Buendel im Cache: die Sperre wieder loesen, sonst
      // blieben die Symbole fuer den Rest der Sitzung leer.
      holt = null;
      console.error("Icon: Nachzuegler-Modul nicht ladbar", e);
      if (typeof window === "undefined") return;
      const wartezeit = WIEDERHOLUNGEN[fehlschlaege];
      if (wartezeit === undefined) {
        // Vorrat aufgebraucht: nicht weiter haemmern. Der naechste Bedarf oder
        // ein "online" faengt nach der Ruhe eine frische Kette an.
        fehlschlaege = 0;
        sperreBis = Date.now() + RUHE_MS;
        return;
      }
      fehlschlaege += 1;
      sperreBis = Date.now() + wartezeit;
      window.setTimeout(() => {
        sperreBis = 0;
        void nachladen();
      }, wartezeit);
    });
  return holt;
}

// Der haeufigste Grund fuer den Fehlschlag ist die abgerissene Leitung — im
// Aussendienst der Normalfall. Kommt sie zurueck, sofort neu ansetzen, statt
// die Ruhe abzuwarten.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    if (nachzuegler) return;
    fehlschlaege = 0;
    sperreBis = 0;
    void nachladen();
  });
}

function abonnieren(w: () => void) {
  wecker.add(w);
  return () => {
    wecker.delete(w);
  };
}
const standLesen = () => fassung;

// Nach dem Laden der Seite im Leerlauf nachziehen. Absichtlich NICHT sofort:
// waehrend des ersten Aufbaus streitet das Buendel sonst mit Bild und
// Datenabfragen um die Leitung. Wer frueher ein spaetes Symbol braucht,
// stoesst das Laden ohnehin beim ersten Fehlgriff selbst an.
function imLeerlaufNachziehen() {
  if (typeof window === "undefined") return;
  const anstossen = () => {
    const ric = (window as Window & {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (ric) ric(() => void nachladen(), { timeout: 4000 });
    else window.setTimeout(() => void nachladen(), 1200);
  };
  if (document.readyState === "complete") anstossen();
  else window.addEventListener("load", anstossen, { once: true });
}
imLeerlaufNachziehen();

// Absicherung gegen den teuersten Fehler dieses Umbaus: ein vertippter Name
// faellt nicht beim Uebersetzen auf, sondern als leere Stelle im Bild. Darum
// im Entwicklungsmodus einmal die per grep aus dem Quellbaum gezogene Liste
// aller geschriebenen Icon-Namen gegen BEIDE Haelften pruefen.
function registryPruefen() {
  // Stand 27.08.2026: aus name= an <Icon>-Marken sowie icon=/icon: gezogen,
  // Ternaere aufgeloest, Vergleichsoperanden (x === "…") ausgesiebt.
  // Momentaufnahme — neue Namen kommen hier nicht von selbst hinein; die
  // Liste faengt den Umbaufehler ab, nicht jede kuenftige Nachlaessigkeit.
  const WACHLISTE = [
    "align-center-horizontal", "align-left", "align-right", "arrow-counter-clockwise", "arrow-left",
    "arrow-right", "arrows-clockwise", "bezier-curve", "binoculars", "blueprint", "bookmark-simple", "camera",
    "camera-plus", "caret-down", "caret-left", "caret-right", "caret-up", "chart-bar", "chart-donut",
    "chart-line", "chart-line-up", "chart-polar", "chat-circle-dots", "check", "check-circle", "check-square",
    "circle", "clock-counter-clockwise", "cloud-check", "code", "command", "compass", "copy",
    "crosshair-simple", "cube", "cursor", "cursor-click", "database", "diamond", "dots-six-vertical",
    "dots-three-vertical", "download-simple", "envelope-open", "envelope-simple", "export", "eye", "eye-slash",
    "eyedropper-sample", "file", "file-pdf", "file-text", "file-xls", "file-zip", "fire", "floppy-disk",
    "folder-minus", "folder-open", "folder-simple", "folder-simple-plus", "folder-simple-star", "folders",
    "fork-knife", "frame-corners", "funnel", "funnel-simple", "funnel-x", "gauge", "git-branch", "globe",
    "globe-hemisphere-west", "hamburger", "hash", "house", "identification-card", "image", "images", "info",
    "key", "layout", "lightning", "link", "link-break", "list", "list-checks", "list-dashes", "lock-key",
    "lock-simple", "magnifying-glass", "magnifying-glass-plus", "map-pin", "map-pin-area", "map-pin-plus",
    "map-trifold", "minus", "moon", "paint-brush", "paint-brush-broad", "paper-plane-tilt", "paperclip",
    "pencil-ruler", "pencil-simple", "pencil-simple-line", "person-simple-walk", "play", "plugs",
    "plugs-connected", "plus", "plus-circle", "plus-square", "polygon", "printer", "prohibit", "question",
    "rows", "ruler", "scales", "scribble-loop", "seal-check", "selection-plus", "share-network", "shield-star",
    "shuffle", "sign-in", "sign-out", "sliders-horizontal", "sort-ascending", "sort-descending", "sparkle",
    "square", "squares-four", "stack", "stack-simple", "star", "star-fill", "storefront", "sun", "swatches",
    "table", "text-aa", "textbox", "traffic-signal", "trash", "tray-arrow-up", "tree", "triangle",
    "upload-simple", "user", "user-circle", "user-plus", "users-three", "vector-two", "warning",
    "warning-circle", "warning-diamond", "wrench", "x", "x-circle",
  ];
  const fehlend = WACHLISTE.filter((n) => !(n in SOFORT) && !(nachzuegler && n in nachzuegler));
  if (fehlend.length) {
    // Diese elf waren schon VOR der Teilung ohne Eintrag und zeichnen sich
    // heute als leere Stelle: cursor-click, file-text, floppy-disk,
    // folder-simple-plus, magnifying-glass-plus, play, plugs, share-network,
    // swatches, traffic-signal, warning-diamond. Bewusst nicht mitrepariert —
    // das waere sichtbare Oberflaeche, nicht Ladezeit.
    console.warn(`[Icon] ${fehlend.length} benutzte(r) Name(n) ohne Symbol in der Registry: ${fehlend.join(", ")}`);
  }
}

export interface IconProps {
  name: string;
  /** Accepted for port parity. The prototype's icon font always rendered the
   *  regular weight (its glyph map was built from it), so the approved design
   *  renders are all-regular — we match that. */
  weight?: IconWeight | string;
  size?: number;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

export function Icon({ name, size = 18, className = "", style = {}, title }: IconProps) {
  // Weckt genau die Stellen, die noch auf ein nachgeladenes Symbol warten.
  useSyncExternalStore(abonnieren, standLesen, standLesen);
  const Cmp = SOFORT[name] ?? nachzuegler?.[name];
  const fehlt = !Cmp;

  // Das Nachladen stand frueher im Rumpf des Renderns. Es war idempotent und
  // funktionierte deshalb — aber Rendern muss frei von Nebenwirkungen sein:
  // React 19 darf einen Durchlauf nebenlaeufig verwerfen und wiederholen, und
  // StrictMode tut es ohnehin. Darum steht der Anstoss jetzt im Effekt, also
  // erst nach dem Festschreiben. Das Symbol erscheint unveraendert von selbst,
  // sobald der Nachzuegler da ist: dessen wecker-Runde stoesst ueber
  // useSyncExternalStore ein Neuzeichnen an, und bis dahin haelt der
  // masshaltige Platzhalter unten die Stelle offen.
  useEffect(() => {
    if (!fehlt) return;
    // Absichtlich die Modulvariable lesen, nicht den Renderstand: waehrend des
    // Festschreibens kann der Nachzuegler bereits eingetroffen sein.
    if (!nachzuegler) {
      void nachladen();
    } else if (import.meta.env.DEV) {
      console.warn(`Icon not in registry: ${name}`);
    }
  }, [fehlt, name]);

  // Bewusst gegen Cmp geprueft, nicht gegen fehlt: nur so verengt TypeScript
  // den Typ fuer den Zweig darunter.
  if (!Cmp) {
    // Masshaltiger, unsichtbarer Platzhalter: gleiche Kantenlaenge und dieselbe
    // Flex-Eigenschaft wie das echte Symbol, damit die Zeile nicht springt,
    // wenn es eintrifft.
    return <span aria-hidden style={{ width: size, height: size, display: "inline-block", verticalAlign: "middle", flex: "none" }} />;
  }
  return (
    <Cmp
      size={size}
      weight="regular"
      className={className}
      style={{ verticalAlign: "middle", flex: "none", ...style }}
      aria-hidden={title ? undefined : true}
      alt={title}
    />
  );
}
